// src/context/ThemeContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { DEFAULT_ORG_ID } from "../utils/brandAssets";

const DARK_KEY = "darkMode";
const darkKeyForOrg = (orgId) => `${DARK_KEY}__${orgId || DEFAULT_ORG_ID}`;
const readStoredDarkMode = (orgId) => {
  try {
    const scoped = localStorage.getItem(darkKeyForOrg(orgId));
    if (scoped === "true" || scoped === "false") return scoped === "true";
    return localStorage.getItem(DARK_KEY) === "true";
  } catch {
    return false;
  }
};

const ThemeContext = createContext({
  isDark: false,
  mode: "light",
  setDark: (_v) => {},
  setMode: (_v) => {},
  toggle: () => {},
});

export function ThemeProvider({ children }) {
  const { user, activeOrgId } = useAuth();
  const resolvedOrgId = activeOrgId || user?.orgId || DEFAULT_ORG_ID;
  const [isDark, setIsDark] = useState(() => readStoredDarkMode(DEFAULT_ORG_ID));

  useEffect(() => {
    setIsDark(readStoredDarkMode(resolvedOrgId));
  }, [resolvedOrgId]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const mode = isDark ? "dark" : "light";
    root.classList.toggle("dark", isDark);
    body.classList.toggle("dark", isDark);
    root.setAttribute("data-theme", mode);
    body.setAttribute("data-theme", mode);
    try {
      localStorage.setItem(darkKeyForOrg(resolvedOrgId), String(isDark));
      localStorage.setItem(DARK_KEY, String(isDark)); // legacy/global fallback
    } catch {}
    // notify any listeners
    try {
      window.dispatchEvent(new CustomEvent("nia-theme", { detail: { isDark, mode } }));
    } catch {}
  }, [isDark, resolvedOrgId]);

  const value = useMemo(
    () => ({
      isDark,
      mode: isDark ? "dark" : "light",
      setDark: (v) => setIsDark(Boolean(v)),
      setMode: (mode) => setIsDark(mode === "dark"),
      toggle: () => setIsDark((d) => !d),
    }),
    [isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
